import { Component, Input, OnInit } from '@angular/core';
import { ModalController, AlertController, ToastController, ActionSheetController } from '@ionic/angular';
import { JavapiService } from '../../services/javapi.service';
import { MetodosgenService } from '../../services/metodosgen.service';
import { firstValueFrom } from 'rxjs';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Metodosui } from '../../services/metodosui';

@Component({
  selector: 'app-galeria-trabajos',
  templateUrl: './galeria-trabajos.component.html',
  styleUrls: ['./galeria-trabajos.component.scss'],
  standalone: false
})
export class GaleriaTrabajosComponent implements OnInit {

  @Input() trabajo: any;

  segment: string = 'GENERAL'; // GENERAL, ANTES, DESPUES

  details: any[] = [];
  filteredDetails: any[] = [];

  constructor(
    private modalCtrl: ModalController,
    public javapiService: JavapiService,
    private metodosGenService: MetodosgenService,
    private metodosUi: Metodosui,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private actionSheetCtrl: ActionSheetController
  ) { }

  ngOnInit() {
    this.loadDetails();
  }

  async loadDetails() {
    try {
      if (!this.trabajo || !this.trabajo.id) return;

      const res: any = await firstValueFrom(
        this.javapiService.getCommonModel(
          this.metodosGenService.setModeloGeneral(
            "genModel", "trabajosGaleria", String(this.trabajo.id), "", "", "", "", "", "", "", "", "", "", "", "", "", ""
          )
        )
      );

      if (Array.isArray(res)) {
        this.details = res.map(d => ({
          id: d.dt1,
          clasificacion: d.dt2,
          imagen_path: d.dt3, // Filename
          texto: d.dt4,
          fecha: d.dt5,
          tipo: (d.dt3 && d.dt3.length > 5) ? 'IMAGEN' : 'NOTA',
          source: d.dt15 || 'galeria' // 'galeria' | 'inspeccion'
        }));
        this.filterDetails();
      } else {
        this.details = [];
        this.filterDetails();
      }

    } catch (error) {
      console.error("Error loading details", error);
    }
  }

  filterDetails() {
    this.filteredDetails = this.details.filter(d => d.clasificacion === this.segment);
  }

  segmentChanged(ev: any) {
    this.segment = ev.detail.value;
    this.filterDetails();
  }

  close() {
    this.modalCtrl.dismiss();
  }

  /**
   * Resuelve el path base para obtener la imagen según su origen.
   * @param source - 'galeria' | 'inspeccion' | 'medida'
   */
  getImagePath(source: string): string {
    switch (source) {
      case 'inspeccion': return 'path_img_inspecciones';
      case 'medida': return 'path_img_medidas';
      default: return 'path_img_trabajos';
    }
  }

  // addNote removed

  async openOptions(item: any) {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Opciones',
      cssClass: 'custom-action-sheet',
      buttons: [
        {
          text: 'Editar Texto',
          icon: 'pencil',
          handler: () => {
            this.editItem(item);
          }
        },
        {
          text: 'Mover de Categoría',
          icon: 'swap-horizontal',
          handler: () => {
            this.moveClassification(item);
          }
        },
        {
          text: 'Eliminar',
          icon: 'trash',
          role: 'destructive',
          handler: () => {
            this.deleteItem(item);
          }
        },
        {
          text: 'Cancelar',
          icon: 'close',
          role: 'cancel'
        }
      ]
    });
    await actionSheet.present();
  }

  async editItem(item: any) {
    const alert = await this.alertCtrl.create({
      header: 'Editar Descripción',
      cssClass: 'custom-alert',
      inputs: [
        {
          name: 'caption',
          type: 'textarea',
          value: item.texto,
          placeholder: 'Escribe el texto...'
        }
      ],
      buttons: [
        { text: 'Cancelar', role: 'cancel', cssClass: 'alert-button btn-cancel' },
        {
          text: 'Guardar',
          cssClass: 'alert-button success-btn',
          handler: (data) => {
            this.updateDetail(item.id, data.caption || '', item.imagen_path);
          }
        }
      ]
    });
    await alert.present();
  }

  async deleteItem(item: any) {
    const confirm = await this.metodosUi.showConfirmation('Confirmar Eliminación', '¿Estás seguro de que deseas eliminar este registro?', 'Eliminar', true);
    if (confirm) {
      this.updateDetail(item.id, '', 'DELETE');
    }
  }

  async addPhoto() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Seleccionar Imagen',
      cssClass: 'custom-action-sheet',
      buttons: [
        {
          text: 'Cámara',
          icon: 'camera',
          handler: () => this.takePhoto(CameraSource.Camera)
        },
        {
          text: 'Galería',
          icon: 'image',
          handler: () => this.takePhoto(CameraSource.Photos)
        },
        { text: 'Cancelar', role: 'cancel', icon: 'close' }
      ]
    });
    await actionSheet.present();
  }

  async takePhoto(source: CameraSource) {
    try {
      if (source === CameraSource.Photos) {
        const result = await Camera.pickImages({
          quality: 80,
          limit: 10
        });

        if (result.photos && result.photos.length > 0) {
          await this.metodosUi.showLoading('Procesando imágenes...');
          const loadedBase64s: string[] = [];
          for (const photo of result.photos) {
            if (photo.webPath) {
              const response = await fetch(photo.webPath);
              const blob = await response.blob();
              const base64 = await this.convertBlobToBase64(blob) as string;
              const pureBase64 = base64.split(',')[1] || base64;
              loadedBase64s.push(pureBase64);
            }
          }
          await this.metodosUi.hideLoading();

          if (loadedBase64s.length > 0) {
            await this.saveMultipleDetails(loadedBase64s, '');
          }
        }
      } else {
        const image = await Camera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: source
        });

        if (image.base64String) {
          await this.saveDetail('IMAGEN', '', image.base64String);
        }
      }
    } catch (e) {
      console.error("User cancelled or error", e);
      await this.metodosUi.hideLoading();
    }
  }

  convertBlobToBase64(blob: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });
  }

  async saveMultipleDetails(base64s: string[], texto: string) {
    try {
      await this.metodosUi.showLoading('Subiendo imágenes...');
      for (const base64 of base64s) {
        const payload = this.metodosGenService.setModeloGeneral(
          "TRABAJOS_GALERIA", "INSERT",
          "0", String(this.trabajo.id),
          this.segment, base64, texto,
          "", "", "", "", "", "", "", "", "", ""
        );
        await firstValueFrom(this.javapiService.saveTrabajoGaleriaArchivo(payload));
      }
      await this.metodosUi.hideLoading();
      this.toastCtrl.create({ message: 'Imágenes subidas exitosamente', duration: 2000, color: 'success' }).then(t => t.present());
      this.loadDetails();
    } catch (error) {
      await this.metodosUi.hideLoading();
      console.error("Error saving multiple details", error);
      this.toastCtrl.create({ message: 'Error de conexión al subir lote', duration: 2000, color: 'danger' }).then(t => t.present());
    }
  }

  async saveDetail(tipo: string, texto: string, imagen: string) {
    try {
      // payload structure for galeria: dt1=id(0), dt2=trabajo_id, dt3=clasificacion, dt4=imagen_path, dt5=texto
      const payload = this.metodosGenService.setModeloGeneral(
        "TRABAJOS_GALERIA", "INSERT",
        "0", String(this.trabajo.id),
        this.segment, imagen, texto,
        "", "", "", "", "", "", "", "", "", ""
      );

      const res: any = await firstValueFrom(this.javapiService.saveTrabajoGaleriaArchivo(payload));

      if (res && res.success) {
        this.toastCtrl.create({ message: 'Guardado exitosamente', duration: 2000, color: 'success' }).then(t => t.present());
        this.loadDetails();
      } else {
        this.toastCtrl.create({ message: 'Error al guardar', duration: 2000, color: 'danger' }).then(t => t.present());
      }

    } catch (error) {
      console.error("Error saving", error);
      this.toastCtrl.create({ message: 'Error de conexión', duration: 2000, color: 'danger' }).then(t => t.present());
    }
  }

  async updateDetail(id: string, texto: string, actionPath: string) {
    try {
      const payload = this.metodosGenService.setModeloGeneral(
        "TRABAJOS_GALERIA", actionPath === 'DELETE' ? "DELETE" : "UPDATE",
        String(id), String(this.trabajo.id),
        this.segment, actionPath, texto,
        "", "", "", "", "", "", "", "", "", ""
      );

      const res: any = await firstValueFrom(this.javapiService.saveTrabajoGaleriaArchivo(payload));

      if (res && res.success) {
        const msg = actionPath === 'DELETE' ? 'Eliminado exitosamente' : 'Actualizado exitosamente';
        this.toastCtrl.create({ message: msg, duration: 2000, color: 'success' }).then(t => t.present());
        this.loadDetails();
      } else {
        this.toastCtrl.create({ message: 'Error procesando solicitud', duration: 2000, color: 'danger' }).then(t => t.present());
      }
    } catch (error) {
      console.error("Error updating", error);
      this.toastCtrl.create({ message: 'Error de conexión', duration: 2000, color: 'danger' }).then(t => t.present());
    }
  }

  async moveClassification(item: any) {
    const classifications = ['GENERAL', 'ANTES', 'DESPUES'].filter(c => c !== item.clasificacion);
    
    const buttons = classifications.map(c => ({
      text: `Mover a ${c}`,
      icon: 'arrow-forward-circle-outline',
      handler: () => {
        this.updateClassification(item, c);
      }
    }));

    buttons.push({
      text: 'Cancelar',
      icon: 'close',
      role: 'cancel'
    } as any);

    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Mover Imagen',
      cssClass: 'custom-action-sheet',
      buttons: buttons
    });
    await actionSheet.present();
  }

  async updateClassification(item: any, newClassification: string) {
    try {
      await this.metodosUi.showLoading('Moviendo imagen...');
      const payload = this.metodosGenService.setModeloGeneral(
        "TRABAJOS_GALERIA", "UPDATE",
        String(item.id), String(this.trabajo.id),
        newClassification, item.imagen_path, item.texto || '',
        "", "", "", "", "", "", "", "", "", ""
      );

      const res: any = await firstValueFrom(this.javapiService.saveTrabajoGaleriaArchivo(payload));
      await this.metodosUi.hideLoading();

      if (res && res.success) {
        this.toastCtrl.create({ message: `Movido a ${newClassification} exitosamente`, duration: 2000, color: 'success' }).then(t => t.present());
        this.loadDetails();
      } else {
        this.toastCtrl.create({ message: 'Error al mover imagen', duration: 2000, color: 'danger' }).then(t => t.present());
      }
    } catch (error) {
      await this.metodosUi.hideLoading();
      console.error("Error moving classification", error);
      this.toastCtrl.create({ message: 'Error de conexión', duration: 2000, color: 'danger' }).then(t => t.present());
    }
  }

  async viewImage(item: any, imageSrc?: string, startInCropMode: boolean = false) {
    const imagesForSlider = this.filteredDetails.map(d => ({
      id: d.id,
      itemData: d,
      path: d.imagen_path,
      source: d.source || 'galeria',
      texto: d.texto || 'Vista Previa'
    }));

    const startIndex = this.filteredDetails.findIndex(d => d.id === item.id);

    const { VerimagenComponent } = await import('../verimagen/verimagen.component');
    const res: any = await this.metodosUi.openModal(VerimagenComponent, {
      images: imagesForSlider,
      startIndex: startIndex >= 0 ? startIndex : 0,
      enableCrop: true,
      startInCropMode: startInCropMode
    });

    if (res && (res.refresh || res.croppedBase64)) {
      if (res.croppedBase64) {
        const targetItem = res.imageItem?.itemData || item;
        await this.updateItemImage(targetItem, res.croppedBase64);
      } else {
        this.loadDetails();
      }
    }
  }

  async updateItemImage(item: any, base64: string) {
    try {
      await this.metodosUi.showLoading('Guardando imagen editada...');
      const payload = this.metodosGenService.setModeloGeneral(
        "TRABAJOS_GALERIA", "UPDATE",
        String(item.id), String(this.trabajo.id),
        item.clasificacion || this.segment, base64, item.texto || '',
        "", "", "", "", "", "", "", "", "", ""
      );

      const res: any = await firstValueFrom(this.javapiService.saveTrabajoGaleriaArchivo(payload));
      await this.metodosUi.hideLoading();

      if (res && res.success) {
        this.toastCtrl.create({ message: 'Imagen editada y guardada exitosamente', duration: 2000, color: 'success' }).then(t => t.present());
        this.loadDetails();
      } else {
        this.toastCtrl.create({ message: 'Error al guardar imagen editada', duration: 2000, color: 'danger' }).then(t => t.present());
      }
    } catch (error) {
      await this.metodosUi.hideLoading();
      console.error("Error saving edited image", error);
      this.toastCtrl.create({ message: 'Error de conexión al guardar imagen', duration: 2000, color: 'danger' }).then(t => t.present());
    }
  }
}
